// frontend/src/modules/payroll/components/RecurringComponentsPanel.tsx
//
// Per-employee recurring earnings/deductions manager. The payroll engine
// materializes active rows into every computed run automatically.
import * as React from 'react';
import { toast } from 'sonner';
import { Plus, Repeat } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { useApi } from '../../../design-system/useApi';
import { formatMoney } from '../../../design-system/format';
import { VISUAL, statusPill, statusDot } from '../../../design-system/visual';
import { payrollApi } from '../api';
import type { RecurringComponent, RecurringComponentType, RecurringCalc } from '../types';
import type { EmployeeWithUser } from '../../hr/types';

export function RecurringComponentsPanel({ currency }: { currency: string }) {
  const rowsQ = useApi<{ rows: RecurringComponent[] }>('/api/payroll/recurring-components');
  const empQ = useApi<{ employees: EmployeeWithUser[] }>('/api/hr/employees');
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<RecurringComponent | null>(null);
  const [busyId, setBusyId] = React.useState<string | null>(null);

  const rows = rowsQ.data?.rows ?? [];
  const employees = empQ.data?.employees ?? [];
  const empById: Record<string, EmployeeWithUser> = {};
  for (const e of employees) empById[e.id] = e;

  const byEmployee: Record<string, RecurringComponent[]> = {};
  for (const r of rows) (byEmployee[r.employee_id] ||= []).push(r);

  async function toggleActive(row: RecurringComponent) {
    setBusyId(row.id);
    try {
      await payrollApi.updateRecurringComponent(row.id, { active: !row.active });
      toast.success(row.active ? 'Deactivated' : 'Reactivated');
      rowsQ.reload();
    } catch (err: any) {
      toast.error('Update failed', { description: err.message });
    } finally { setBusyId(null); }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-[13px] text-muted-foreground max-w-xl">
          Recurring lines are added automatically every time a run is computed — fixed amounts
          or a percentage of the employee's base pay, optionally limited to an effective date range.
        </p>
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5 shrink-0">
          <Plus className="h-3.5 w-3.5" /> Add recurring line
        </Button>
      </div>

      {rowsQ.loading ? (
        <div className={[VISUAL.bento, 'p-10 text-center text-[13px] text-muted-foreground'].join(' ')}>Loading…</div>
      ) : rows.length === 0 ? (
        <div className={[VISUAL.bento, 'py-14 px-8 text-center'].join(' ')}>
          <div className="mx-auto mb-5 h-14 w-14 rounded-2xl bg-saffron/15 ring-1 ring-saffron/25 flex items-center justify-center">
            <Repeat className="h-6 w-6 text-saffron-foreground" strokeWidth={1.6} />
          </div>
          <p className="text-[16px] font-semibold tracking-[-0.01em] text-foreground">No recurring components</p>
          <p className="text-[13px] text-muted-foreground mt-1.5 max-w-sm mx-auto">
            Add allowances, stipends, or standing deductions once — they'll appear on every future run.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {Object.entries(byEmployee).map(([employeeId, comps]) => (
            <section key={employeeId} className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
              <div className="px-5 sm:px-6 py-3.5 border-b border-border/40 flex items-center gap-3">
                <div className="h-8 w-8 rounded-xl bg-saffron/15 ring-1 ring-saffron/30 flex items-center justify-center text-[12px] font-bold text-saffron-foreground shrink-0">
                  {(empById[employeeId]?.name?.[0] ?? '?').toUpperCase()}
                </div>
                <p className="text-[13.5px] font-semibold text-foreground truncate">
                  {empById[employeeId]?.name ?? employeeId.slice(0, 8)}
                </p>
              </div>
              <ul className="divide-y divide-border/30">
                {comps.map((row) => (
                  <li key={row.id} className="flex items-center gap-4 px-5 sm:px-6 py-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-[13.5px] font-semibold text-foreground truncate">
                        {row.name}
                        <Badge variant="secondary" className="ml-2 text-[10px] uppercase tracking-wide">
                          {row.type}
                        </Badge>
                      </p>
                      <p className="text-[12px] text-muted-foreground mt-0.5">
                        {row.calc === 'percent_of_base'
                          ? `${Number(row.amount)}% of base`
                          : formatMoney(row.amount, currency)}
                        {row.effective_from ? ` · from ${row.effective_from.slice(0, 10)}` : ''}
                        {row.effective_to ? ` · until ${row.effective_to.slice(0, 10)}` : ''}
                      </p>
                    </div>
                    {row.active
                      ? <span className={statusPill('success')}><span className={statusDot('success')} />Active</span>
                      : <span className={statusPill('muted')}><span className={statusDot('muted')} />Inactive</span>}
                    <Button size="sm" variant="ghost" className="rounded-full h-8 text-[12px]"
                      onClick={() => { setEditing(row); setDialogOpen(true); }}>
                      Edit
                    </Button>
                    <Button size="sm" variant="outline" disabled={busyId === row.id} className="rounded-full h-8 text-[12px]"
                      onClick={() => toggleActive(row)}>
                      {row.active ? 'Deactivate' : 'Reactivate'}
                    </Button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      <RecurringDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        employees={employees}
        editing={editing}
        onSaved={() => { setDialogOpen(false); rowsQ.reload(); }}
      />
    </div>
  );
}

function RecurringDialog({
  open, onOpenChange, employees, editing, onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  employees: EmployeeWithUser[];
  editing: RecurringComponent | null;
  onSaved: () => void;
}) {
  const [employeeId, setEmployeeId] = React.useState('');
  const [type, setType] = React.useState<RecurringComponentType>('earning');
  const [name, setName] = React.useState('');
  const [calc, setCalc] = React.useState<RecurringCalc>('fixed');
  const [amount, setAmount] = React.useState('');
  const [from, setFrom] = React.useState('');
  const [to, setTo] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    if (editing) {
      setEmployeeId(editing.employee_id);
      setType(editing.type);
      setName(editing.name);
      setCalc(editing.calc);
      setAmount(String(Number(editing.amount)));
      setFrom(editing.effective_from?.slice(0, 10) ?? '');
      setTo(editing.effective_to?.slice(0, 10) ?? '');
    } else {
      setEmployeeId(employees[0]?.id ?? '');
      setType('earning'); setName(''); setCalc('fixed'); setAmount(''); setFrom(''); setTo('');
    }
  }, [open, editing, employees]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!employeeId || !name.trim() || !amount) {
      toast.error('Employee, name, and amount are required');
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        await payrollApi.updateRecurringComponent(editing.id, {
          type, name: name.trim(), calc, amount: Number(amount),
          effective_from: from || null, effective_to: to || null,
        });
        toast.success('Recurring line updated');
      } else {
        await payrollApi.createRecurringComponent({
          employee_id: employeeId, type, name: name.trim(), calc, amount: Number(amount),
          effective_from: from || null, effective_to: to || null,
        });
        toast.success('Recurring line added');
      }
      onSaved();
    } catch (err: any) {
      toast.error('Save failed', { description: err.message });
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{editing ? 'Edit recurring line' : 'Add recurring line'}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-4 pt-2">
          <div className="grid gap-1.5">
            <Label>Employee</Label>
            <Select value={employeeId} onValueChange={setEmployeeId} disabled={!!editing}>
              <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>
                {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label>Type</Label>
              <Select value={type} onValueChange={(v) => setType(v as RecurringComponentType)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="earning">Earning</SelectItem>
                  <SelectItem value="deduction">Deduction</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Housing allowance" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label>Calculation</Label>
              <Select value={calc} onValueChange={(v) => setCalc(v as RecurringCalc)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Fixed amount</SelectItem>
                  <SelectItem value="percent_of_base">% of base pay</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>{calc === 'percent_of_base' ? 'Percent (%)' : 'Amount'}</Label>
              <Input type="number" min={0} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label>Effective from <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label>Effective until <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? 'Saving…' : editing ? 'Save changes' : 'Add line'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
